import { Box, Grid, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { useIntl } from 'react-intl'
import { useCompany } from 'contexts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

import { SurveyStatus as PulseSurveyStatus } from 'enums/survey'

type ProgressBarBulletProps = {
  filled: boolean
}

const ProgressBarBullet = styled('div')<ProgressBarBulletProps>(
  ({ theme, filled }) => ({
    width: '20px',
    height: '20px',
    borderRadius: '100%',
    zIndex: 995,
    backgroundColor: filled ? theme.palette.primary.main : '#CCC',
    border: '3px solid #f9f9f9',
    flexShrink: 0,
  }),
)

export default function PulseSurveyOverviewProgress({
  selectedPulseSurvey,
}: any) {
  const intl = useIntl()
  const { status, created_at, open_at, close_at, actually_closed_at } = selectedPulseSurvey
  const { company, } = useCompany()!
  const closeAt = (actually_closed_at === null) ?  close_at : actually_closed_at

  const formatTime = (value: string) =>
    dayjs(value).tz(company.timezone).format('h:mm A z')

  const isOpenPending =
    status === PulseSurveyStatus.AwaitingManualOpening || !open_at

  const bulletFilled = [
    true,
    status === PulseSurveyStatus.Open || status === PulseSurveyStatus.Close,
    status === PulseSurveyStatus.Close,
  ]

  const percentage =
    status === PulseSurveyStatus.Scheduled ||
    status === PulseSurveyStatus.AwaitingManualOpening
      ? 8
      : status === PulseSurveyStatus.Open
      ? 56
      : 100

  const legends = [
    {
      label: intl.formatMessage({
        id: 'surveyProgress.bulletLabels.createdOn',
      }),
      value: dayjs(created_at).tz(company.timezone).format('MMM Do YYYY'),
      time: formatTime(created_at),
    },
    {
      label: intl.formatMessage({
        id: 'surveyProgress.bulletLabels.openOn',
      }),
      value: isOpenPending
        ? intl.formatMessage({ id: 'statuses.pending' })
        : open_at
        ? dayjs(open_at).tz(company.timezone).format('MMM Do YYYY')
        : '--',
      time: isOpenPending ? null : formatTime(open_at),
    },
    {
      label:
        status !== 'close'
          ? intl.formatMessage({
              id: 'surveyProgress.bulletLabels.willCloseOn',
            })
          : intl.formatMessage({
              id: 'surveyProgress.bulletLabels.closedOn',
            }),
      value: closeAt
        ? dayjs(closeAt).tz(company.timezone).format('MMM Do YYYY')
        : 'Open Indefinitely',
      time: closeAt ? formatTime(closeAt) : null,
    },
  ]

  return (
    <>
      {/* sm and up: horizontal progress bar with columns below */}
      <Grid
        container
        alignItems='center'
        sx={{ maxWidth: 500, display: { xs: 'none', sm: 'flex' } }}
      >
        <Grid item xs={12}>
          <Grid
            container
            alignItems='center'
            justifyContent='space-between'
            style={{ position: 'relative' }}
          >
            <Box
              component='div'
              sx={{
                position: 'absolute',
                width: '95%',
                height: '6px',
                backgroundColor: '#ddd',
                margin: '0 auto',
                left: '0px',
                right: '0px',
              }}
            >
              <Box
                component='div'
                sx={{
                  position: 'absolute',
                  width: `${percentage}%`,
                  transition: 'width 0.5s',
                  height: '100%',
                  backgroundColor: 'primary.main',
                }}
              />
            </Box>

            <ProgressBarBullet filled={bulletFilled[0]} />
            <ProgressBarBullet filled={bulletFilled[1]} />
            <ProgressBarBullet filled={bulletFilled[2]} />
          </Grid>
          <Box mt={1} width={1}>
            <Grid container alignItems='center' justifyContent='space-between'>
              {legends.map((legend, index: number) => {
                const desktopAlign =
                  index === 2 ? 'right' : index === 1 ? 'center' : 'left'

                return (
                  <Grid item key={legend.label}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      align={desktopAlign}
                    >
                      {legend.label}
                    </Typography>
                    <Typography
                      variant='body1'
                      color='text.primary'
                      fontWeight={600}
                      align={desktopAlign}
                    >
                      {legend.value}
                    </Typography>
                    {legend.time && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                        align={desktopAlign}
                      >
                        {legend.time}
                      </Typography>
                    )}
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* xs: vertical timeline - a 2-column CSS Grid (rail, content) with one
          explicit row per text line. Grid guarantees the rail cell and the
          content cell of the same row share the exact same height, so the
          bullet (placed in the date's row) lands precisely centered against
          the date text - no flex-basis ambiguity, no manual offsets. */}
      <Box sx={{ display: { xs: 'grid', sm: 'none' }, gridTemplateColumns: '26px 1fr', columnGap: 2, maxWidth: 500 }}>
        {legends.map((legend, index: number) => {
          const isFirst = index === 0
          const isLast = index === legends.length - 1
          const connectorColor = (filled: boolean) => (filled ? 'primary.main' : '#ddd')
          const row = index * 3 + 1

          return (
            <Box key={legend.label} sx={{ display: 'contents' }}>
              <Box sx={{ gridColumn: 1, gridRow: row, display: 'flex', justifyContent: 'center' }}>
                {!isFirst && (
                  <Box sx={{ width: '2px', height: '100%', backgroundColor: connectorColor(bulletFilled[index]) }} />
                )}
              </Box>
              <Typography variant='body2' color='text.secondary' sx={{ gridColumn: 2, gridRow: row }}>
                {legend.label}
              </Typography>

              <Box sx={{ gridColumn: 1, gridRow: row + 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ProgressBarBullet filled={bulletFilled[index]} />
              </Box>
              <Typography variant='body1' color='text.primary' fontWeight={600} sx={{ gridColumn: 2, gridRow: row + 1 }}>
                {legend.value}
              </Typography>

              {(legend.time || !isLast) && (
                <Box sx={{ gridColumn: 1, gridRow: row + 2, display: 'flex', justifyContent: 'center' }}>
                  {!isLast && (
                    <Box sx={{ width: '2px', height: '100%', minHeight: legend.time ? 0 : 16, backgroundColor: connectorColor(bulletFilled[index + 1]) }} />
                  )}
                </Box>
              )}
              {legend.time && (
                <Typography variant='caption' color='text.secondary' sx={{ gridColumn: 2, gridRow: row + 2, pb: isLast ? 0 : 1.5 }}>
                  {legend.time}
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>
    </>
  )
}
